import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AppFacadeService } from '../../../services/app-facade.service';
import { QuickLinkService } from '../../../services/quicklink.service';
import { ConfigurationService } from '../../../services/configuration.service';
import {
    redirectionFailure,
    redirectionSuccess,
    startPublicRedirection,
    startRedirection,
    setRedirectionLoginSession,
    redirectToConsumerPortal,
    redirectToSubdomain,
    redirectConfigSet,
    redirectToPayment,
    processRoute
} from './redirection.actions';
import { ComponentsToLoad } from '../../../enums/components-to-load.enum';
import { QuickLink } from '../../../models/quicklink.model';
import { LoginSession } from '../../../models/login-session.model';
import { LoggerService } from '../../../services/logger.service';
import { ErrorType } from '../../../models/error.model';
import { ErrorService } from '../../../services/error.service';
import { setQuickLinkData } from '../../domains/quicklink/quicklink.actions';
import { loadAccountFeature } from '../../domains/feature-account/account.actions';
import { ConsumerPortalConfig } from '../../../models/consumer-portal-config';
import { AuthKeys } from '../../../enums/local-storage-keys.enum';
import { AppMediatorService } from '../../../services/app-mediator.service';
import { Action } from '@ngrx/store';
import { setConfig } from '../config/config.actions';
import { OrganizationIdService } from '../../../services/organization-id.service';
import { OrgDetail } from '../../../models/organization-detail.model';

@Injectable()
export class RedirectionEffects {
    private readonly actions$ = inject(Actions);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly quickLinkService = inject(QuickLinkService);
    private readonly configService = inject(ConfigurationService);
    private readonly logger = inject(LoggerService);
    private readonly errorService = inject(ErrorService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly organizationIdService = inject(OrganizationIdService);
    /**
     * Generic effect for public (no-auth) routes. Resolves orgId from URL if missing,
     * fetches org config (populates HiddenFeatures for feature toggles), THEN dispatches
     * redirectionSuccess so the component renders with correct feature toggle data.
     *
     * Reusable for any future unauthenticated flow (asset tag, public product pages, etc.).
     *
     * Flow: resolve orgId → getCpOrgConfig → consolidateConfig → dispatch [setConfig, redirectionSuccess]
     * By the time the reducer sets toLoad and the template renders the component,
     * FeatureToggleService.hiddenFeatures is already populated.
     */
    startPublicRedirection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startPublicRedirection),
            tap(() => this.appFacadeService.setLoading(true)),
            switchMap(({ redirection, cpInput }) => {
                const resolveOrgId$ = redirection.orgId ? of(redirection.orgId) : this.resolveOrgIdFromUrl(cpInput);

                return resolveOrgId$.pipe(
                    switchMap(orgId => {
                        if (!orgId) {
                            return of(redirectionFailure({ error: 'OrgResolutionFailed' }));
                        }

                        return this.configService.getCpOrgConfig({ ...cpInput, organizationId: orgId }).pipe(
                            concatMap(orgConfig => {
                                const header = { ...orgConfig?.header };
                                if (cpInput?.header) {
                                    Object.keys(cpInput?.header).forEach(key => {
                                        if (cpInput?.header?.[key as keyof typeof cpInput.header]) {
                                            header[key as keyof typeof header] = cpInput.header[key as keyof typeof cpInput.header];
                                        }
                                    });
                                }
                                const config: ConsumerPortalConfig = this.configService.consolidateConsumerPortalConfig(undefined, {
                                    ...cpInput,
                                    ...orgConfig,
                                    header
                                });
                                // Config (with HiddenFeatures) is set BEFORE redirectionSuccess fires,
                                // so the component renders with correct feature toggle data.
                                return [setConfig({ config }), redirectConfigSet({ isConfigSet: true }), redirectionSuccess({ redirection: { ...redirection, orgId }, cpInput: config })];
                            }),
                            catchError(error => {
                                this.logger.logError(error);
                                // Org config failed but still render the component — feature toggle defaults to enabled
                                return [redirectionSuccess({ redirection: { ...redirection, orgId }, cpInput })];
                            })
                        );
                    }),
                    catchError(() => of(redirectionFailure({ error: 'OrgResolutionFailed' })))
                );
            })
        )
    );

    startRedirection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startRedirection),
            tap(() => this.appFacadeService.setLoading(true)),
            tap(() => this.appMediatorService.localStorageService.removeSessionItem(AuthKeys.User)), // Clear session storage, so that new data get available on interceptor
            switchMap(({ redirection, cpInput }) => {
                // Handle Payment Token
                if (redirection.toLoad === ComponentsToLoad.Payment && cpInput?.Auth?.accessToken) {
                    // Store Auth to localStorage (matches quicklink pattern)
                    this.appMediatorService.localStorageService.persistUser(AuthKeys.User, cpInput);

                    // Dispatch actions
                    return [setConfig({ config: cpInput }), redirectionSuccess({ redirection, cpInput })];
                }

                if (redirection.toLoad !== ComponentsToLoad.QuickLink) {
                    return of(redirectionSuccess({ redirection, cpInput }));
                }

                // Handle QuickLink
                return this.quickLinkService.getConsumerData().pipe(
                    concatMap(quicklinkData => {
                        const authUser = quicklinkData as unknown as QuickLink;
                        // Set Consumer Portal Configurations
                        cpInput = this.configService.consolidateConsumerPortalConfig(undefined, {
                            ...cpInput,
                            // Note camelCase: DB Model
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            Auth: authUser,
                            customerId: authUser?.CustomerId,
                            contractId: authUser.ContractId,
                            updatedContractId: authUser.UpdatedContractId,
                            organizationId: authUser?.OrganizationId,
                            organizationType: authUser.OrganizationType
                        });

                        // Dispatch multiple actions as a flattened stream
                        const actions: Action[] = [
                            setQuickLinkData({ quickLinkData: authUser }),
                            setConfig({ config: cpInput }),
                            setRedirectionLoginSession({
                                session: {
                                    ...authUser,
                                    customerId: authUser.CustomerId,
                                    initialLogin: true
                                } as LoginSession
                            }),
                            redirectionSuccess({
                                redirection: {
                                    ...redirection,
                                    toLoad: ComponentsToLoad.ConsumerPortal,
                                    orgId: authUser.OrganizationId
                                },
                                cpInput
                            })
                        ];

                        // QL never reaches handleRedirection(), the only other loadAccountFeature dispatch site, so
                        // account permissions were never fetched and `accountFeatures` stayed null all session.
                        // Must come after setRedirectionLoginSession — that is what persists the QL token to session
                        // storage (cleared above), and the permissions request reads its auth header from there.
                        // Stock QLs carry no CustomerId; '0' is the getpdf placeholder, not a real account.
                        const customerId = authUser?.CustomerId;
                        if (customerId && customerId !== '0') {
                            actions.push(loadAccountFeature({ customerId }));
                        }

                        return actions;
                    }),
                    catchError(() => of(redirectionFailure({ error: 'FailedExtractQuickLink' })))
                );
            })
        )
    );

    redirectionSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(redirectionSuccess),
            switchMap(({ redirection, cpInput }) => {
                // AssetTag: org config already loaded by startPublicRedirection$ effect.
                // IMPORTANT: Do NOT dispatch redirectToConsumerPortal() — that overwrites
                // toLoad to 'ConsumerPortal', preventing the ngSwitch from matching 'AssetTag'.
                if (redirection.toLoad === ComponentsToLoad.AssetTag) {
                    this.appFacadeService.setLoading(false);
                    return EMPTY;
                }

                // Routed to subdomain
                if (redirection.orgId === '' && redirection.toLoad === ComponentsToLoad.Subdomain) {
                    return of(redirectToSubdomain());
                }

                if (redirection.orgId === '' && redirection.toLoad === ComponentsToLoad.ConsumerPortal) {
                    return of(redirectToConsumerPortal());
                }

                if (redirection.orgId && redirection.toLoad === ComponentsToLoad.Payment) {
                    return of(redirectToPayment());
                }

                // Routed to consumer portal, after getting orgId from subdomain
                return this.configService.getCpOrgConfig({ ...cpInput, organizationId: redirection.orgId }).pipe(
                    concatMap(orgConfig => {
                        // Update OrgConfig

                        // set by key-value pairs from orgConfig.header because some keys are set but have null value
                        const header = { ...orgConfig?.header };
                        if (cpInput?.header) {
                            Object.keys(cpInput?.header).forEach(key => {
                                if (cpInput?.header?.[key as keyof typeof cpInput.header]) {
                                    header[key as keyof typeof header] = cpInput.header[key as keyof typeof cpInput.header];
                                }
                            });
                        }

                        const config: ConsumerPortalConfig = this.configService.consolidateConsumerPortalConfig(undefined, {
                            ...cpInput,
                            ...orgConfig,
                            header
                        });
                        const routeParams = cpInput?.routeTo?.routeParams;
                        if (routeParams && typeof routeParams === 'object') {
                            const { contractIds, customerId } = routeParams;
                            if (Array.isArray(contractIds) && contractIds.length > 0) {
                                const contractId = contractIds[contractIds.length - 1];
                                if (contractId) {
                                    if (customerId) {
                                        this.appFacadeService.dispatchConvertQuoteToReservation(customerId, contractId);
                                    } else {
                                        this.appFacadeService.openContract(contractId);
                                    }
                                }
                            }
                        }
                        return [setConfig({ config }), redirectConfigSet({ isConfigSet: true }), redirectToConsumerPortal()];
                    }),
                    catchError(error => {
                        // If org config not found in the database, then use default and redirect to consumer portal
                        this.logger.logError(error);
                        this.logger.alertDevError(error);
                        this.errorService.setError(ErrorType.InvalidSubDomainOrgId);
                        return of(redirectToConsumerPortal());
                    })
                );
            })
        )
    );

    processRoute$ = createEffect(() =>
        this.actions$.pipe(
            ofType(processRoute),
            mergeMap(({ cpInput, orgId }) => {
                if ((cpInput?.routeTo?.route as ComponentsToLoad) !== ComponentsToLoad.Payment) {
                    // Call the service methods which perform the dispatching internally
                    this.appFacadeService.getloadVersion(orgId);
                    this.appFacadeService.loadLinkAndOpen();
                    this.appFacadeService.loadEsignAndOpen();
                    // Conditionally dispatch other actions
                    if (cpInput?.routeTo?.route && Object.values(ComponentsToLoad).includes(cpInput?.routeTo.route as ComponentsToLoad)) {
                        this.appFacadeService.updateLocation(cpInput?.routeTo.route as ComponentsToLoad);
                        this.appFacadeService.setActiveTab(cpInput?.routeTo.route as ComponentsToLoad);

                        if (cpInput.routeTo.route === ComponentsToLoad.Contracts && cpInput?.routeTo?.routeParams && typeof cpInput?.routeTo?.routeParams === 'string') {
                            this.appFacadeService.openContract(cpInput.routeTo?.routeParams, ComponentsToLoad.Contracts);
                        }
                    }

                    // Return a stream of actions to dispatch (if necessary)
                    return EMPTY as Observable<Action>;
                }

                // If no actions need to be dispatched, return EMPTY
                return of();
            })
        )
    );

    /**
     * Resolves orgId from current URL via public/organizations/url API.
     * Returns orgId string or empty string on failure.
     */
    private resolveOrgIdFromUrl(cpInput?: ConsumerPortalConfig): Observable<string> {
        const orgLookupUrl = window.location.href.includes('localhost') ? cpInput?.defaultSubDomain || window.location.origin : window.location.origin;

        return this.organizationIdService.getOrgIdFromUrl(orgLookupUrl).pipe(
            switchMap(orgResult => {
                if (!orgResult) {
                    return of('');
                }
                const orgDetail = orgResult as unknown as OrgDetail;
                return of(orgDetail?.OrganizationId?.toString() || '');
            }),
            catchError(() => of(''))
        );
    }
}
